import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Box,
  Grid,
  Typography,
  Paper,
  Stack,
  Button,
  Skeleton,
  IconButton,
  Drawer,
  TextField,
  Chip,
} from "@mui/material";
import {
  Filter,
  X,
  Zap,
  Activity,
  Timer,
  TrendingUp,
  PhoneIncoming,
  PhoneOutgoing,
} from "lucide-react";
import {
  ResponsiveContainer,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  RadialBarChart,
  RadialBar,
} from "recharts";
import { motion } from "framer-motion";
import axiosInstance from "../../../../services/axiosInstance";
import SideNavbar from "../../../../components/sideNavbar";
import apiEndPointsConfig from "../../../../services/apiEndPoints";
import EmptyState from "../components/EmptyState";

const COLORS = {
  primary: "#6366F1",
  success: "#10B981",
  warning: "#F59E0B",
  danger: "#EF4444",
  cyan: "#06B6D4",
};

// Animated KPI Card
const KPICard = ({
  title,
  value,
  subtitle,
  icon: Icon,
  color,
  loading,
  trend,
}) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4 }}
  >
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 3,
        border: "1px solid #E2E8F0",
        transition: "all 0.3s ease",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0 12px 24px rgba(0,0,0,0.06)",
        },
      }}
    >
      {loading ? (
        <Stack spacing={1}>
          <Skeleton width="60%" height={20} />
          <Skeleton width="40%" height={32} />
          <Skeleton width="80%" height={16} />
        </Stack>
      ) : (
        <Stack spacing={0.5}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography
              variant="caption"
              sx={{
                color: "#64748B",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: 0.5,
              }}
            >
              {title}
            </Typography>
            <Box
              sx={{
                p: 1,
                borderRadius: 1,
                bgcolor: `${color}15`,
                color: color,
                display: "flex",
              }}
            >
              <Icon size={18} />
            </Box>
          </Stack>
          <Typography variant="h5" sx={{ fontWeight: 800, color: "#1E293B" }}>
            {value}
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            {trend && (
              <Chip
                size="small"
                icon={<TrendingUp size={12} />}
                label={trend}
                sx={{
                  bgcolor: `${COLORS.success}15`,
                  color: COLORS.success,
                  fontWeight: 600,
                  fontSize: "0.7rem",
                  height: 20,
                }}
              />
            )}
            <Typography variant="caption" sx={{ color: "#94A3B8" }}>
              {subtitle}
            </Typography>
          </Stack>
        </Stack>
      )}
    </Paper>
  </motion.div>
);

// Donut Chart for Connection Health
const ConnectionHealthChart = ({ data, loading }) => (
  <Paper
    elevation={0}
    sx={{ p: 2, borderRadius: 3, border: "1px solid #E2E8F0" }}
  >
    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
      Connection Health
    </Typography>
    {loading ? (
      <Skeleton
        variant="circular"
        width={160}
        height={160}
        sx={{ mx: "auto" }}
      />
    ) : (
      <>
        <Box sx={{ height: 200 }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={80}
                paddingAngle={4}
                dataKey="value"
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                formatter={(value, name) => [`${value} calls`, name]}
                contentStyle={{
                  borderRadius: "8px",
                  border: "none",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </Box>
        <Stack spacing={1} sx={{ mt: 2 }}>
          {data.map((item) => (
            <Stack
              key={item.name}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    bgcolor: item.color,
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 600, color: "#475569" }}
                >
                  {item.name}
                </Typography>
              </Stack>
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, color: "#1E293B" }}
              >
                {item.value}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </>
    )}
  </Paper>
);

// Hourly Load Chart
const HourlyLoadChart = ({ data, loading }) => (
  <Paper
    elevation={0}
    sx={{ p: 2, borderRadius: 3, border: "1px solid #E2E8F0" }}
  >
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{ mb: 2 }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        Hourly Load Distribution
      </Typography>
      <Stack direction="row" spacing={2}>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Box
            sx={{
              width: 12,
              height: 3,
              bgcolor: COLORS.primary,
              borderRadius: 1,
            }}
          />
          <Typography variant="caption" sx={{ color: "#64748B" }}>
            Inbound
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Box
            sx={{
              width: 12,
              height: 3,
              bgcolor: COLORS.success,
              borderRadius: 1,
            }}
          />
          <Typography variant="caption" sx={{ color: "#64748B" }}>
            Outbound
          </Typography>
        </Stack>
      </Stack>
    </Stack>
    {loading ? (
      <Skeleton variant="rectangular" height={280} sx={{ borderRadius: 1 }} />
    ) : (
      <Box sx={{ height: 280 }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={data}
            margin={{ top: 10, right: 10, left: -10, bottom: 0 }}
          >
            <defs>
              <linearGradient id="inboundGradient" x1="0" y1="0" x2="0" y2="1">
                <stop
                  offset="5%"
                  stopColor={COLORS.primary}
                  stopOpacity={0.4}
                />
                <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0} />
              </linearGradient>
              <linearGradient id="outboundGradient" x1="0" y1="0" x2="0" y2="1">
                <stop
                  offset="5%"
                  stopColor={COLORS.success}
                  stopOpacity={0.4}
                />
                <stop offset="95%" stopColor={COLORS.success} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid
              strokeDasharray="3 3"
              vertical={false}
              stroke="#E2E8F0"
            />
            <XAxis
              dataKey="name"
              tick={{ fontSize: 11, fill: "#94A3B8" }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 11, fill: "#94A3B8" }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              formatter={(value, name) => [`${value} calls`, name]}
              contentStyle={{
                borderRadius: "8px",
                border: "none",
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
              }}
            />
            <Area
              type="monotone"
              dataKey="inbound"
              stroke={COLORS.primary}
              strokeWidth={2}
              fill="url(#inboundGradient)"
              dot={{ r: 3, fill: COLORS.primary }}
            />
            <Area
              type="monotone"
              dataKey="outbound"
              stroke={COLORS.success}
              strokeWidth={2}
              fill="url(#outboundGradient)"
              dot={{ r: 3, fill: COLORS.success }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </Box>
    )}
  </Paper>
);

// Direction Split Chart
const DirectionSplitChart = ({ inbound, outbound, total, loading }) => {
  const data = [
    { name: "Inbound", value: inbound, fill: COLORS.primary },
    { name: "Outbound", value: outbound, fill: COLORS.success },
  ];
  const inPercent = total > 0 ? Math.round((inbound / total) * 100) : 0;
  const outPercent = total > 0 ? Math.round((outbound / total) * 100) : 0;

  return (
    <Paper
      elevation={0}
      sx={{ p: 2, borderRadius: 3, border: "1px solid #E2E8F0" }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
        Call Direction Split
      </Typography>
      {loading ? (
        <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 1 }} />
      ) : (
        <>
          <Box sx={{ height: 160 }}>
            <ResponsiveContainer width="100%" height="100%">
              <RadialBarChart
                cx="50%"
                cy="50%"
                innerRadius="40%"
                outerRadius="90%"
                data={data}
                startAngle={180}
                endAngle={0}
              >
                <RadialBar dataKey="value" cornerRadius={10} />
                <Tooltip
                  formatter={(value, name) => [`${value} calls`, name]}
                />
              </RadialBarChart>
            </ResponsiveContainer>
          </Box>
          <Stack direction="row" justifyContent="space-around" sx={{ mt: 2 }}>
            <Stack alignItems="center" spacing={0.5}>
              <Box
                sx={{ p: 1, borderRadius: 2, bgcolor: `${COLORS.primary}15` }}
              >
                <PhoneIncoming size={20} color={COLORS.primary} />
              </Box>
              <Typography
                variant="h6"
                sx={{ fontWeight: 800, color: COLORS.primary }}
              >
                {inPercent}%
              </Typography>
              <Typography variant="caption" sx={{ color: "#64748B" }}>
                Inbound
              </Typography>
            </Stack>
            <Stack alignItems="center" spacing={0.5}>
              <Box
                sx={{ p: 1, borderRadius: 2, bgcolor: `${COLORS.success}15` }}
              >
                <PhoneOutgoing size={20} color={COLORS.success} />
              </Box>
              <Typography
                variant="h6"
                sx={{ fontWeight: 800, color: COLORS.success }}
              >
                {outPercent}%
              </Typography>
              <Typography variant="caption" sx={{ color: "#64748B" }}>
                Outbound
              </Typography>
            </Stack>
          </Stack>
        </>
      )}
    </Paper>
  );
};

const TrafficReport = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [filters, setFilters] = useState({ startDate: "", endDate: "" });

  const userId = localStorage.getItem("userId");
  const token = localStorage.getItem("token");

  const fetchData = useCallback(async () => {
    if (!userId || !token) return;
    try {
      setLoading(true);
      const res = await axiosInstance.get(
        `${apiEndPointsConfig.getAllCallLogs}/${userId}?limit=100000&export=true`
      );
      setData(res?.data?.data || []);
    } catch (error) {
      console.error("Fetch Error:", error);
    } finally {
      setLoading(false);
    }
  }, [userId, token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const stats = useMemo(() => {
    if (!data.length)
      return {
        total: 0,
        inCount: 0,
        outCount: 0,
        chartData: [],
        qualityData: [],
        avgDuration: "0s",
        peakHour: "N/A",
        silent: 0,
        short: 0,
        effective: 0,
      };

    let totalSec = 0;
    let inCount = 0;
    let silent = 0;
    let short = 0;
    let effective = 0;
    const hourMap = {};

    const filtered = data.filter((item) => {
      const p = item.payload || {};
      const matches =
        (!filters.startDate || p.callDate >= filters.startDate) &&
        (!filters.endDate || p.callDate <= filters.endDate);

      if (matches) {
        const duration = Number(p.timeDuration) || 0;
        const direction = p.calls_direction;
        const status = p.status;
        const hour = p.calls_time?.split(":")[0] || "00";

        totalSec += duration;
        if (direction === "Inbound") inCount++;

        if (duration === 0 || status === "Did Not Connect") {
          silent++;
        } else if (duration < 30) {
          short++;
        } else {
          effective++;
        }

        if (!hourMap[hour])
          hourMap[hour] = {
            name: `${hour}:00`,
            inbound: 0,
            outbound: 0,
            total: 0,
          };
        hourMap[hour].total++;
        direction === "Inbound"
          ? hourMap[hour].inbound++
          : hourMap[hour].outbound++;
      }
      return matches;
    });

    const total = filtered.length;
    const avgSec = total > 0 ? Math.floor(totalSec / total) : 0;
    const chartData = Object.values(hourMap).sort((a, b) =>
      a.name.localeCompare(b.name),
    );
    const peakEntry = chartData.reduce((p, c) => (p.total > c.total ? p : c), {
      name: "N/A",
    });

    return {
      total,
      inCount,
      outCount: total - inCount,
      avgDuration:
        avgSec > 60
          ? `${Math.floor(avgSec / 60)}m ${avgSec % 60}s`
          : `${avgSec}s`,
      peakHour: peakEntry.name,
      silent,
      short,
      effective,
      chartData,
      qualityData: [
        { name: "Effective (>30s)", value: effective, color: COLORS.success },
        { name: "Short (<30s)", value: short, color: COLORS.warning },
        { name: "Failed/Silent", value: silent, color: COLORS.danger },
      ],
    };
  }, [data, filters]);

  return (
    <Box sx={{ minHeight: "100vh" }}>
      <SideNavbar />
      <Box sx={{ ml: { xs: 0, md: "280px" } }}>
        {/* <Box sx={{ display: { xs: "none", md: "initial" } }}>
          <Header title="Call Traffic Analytics" />
        </Box> */}

        <Box component="main" sx={{ p: { xs: 2, md: 3 } }}>
          {/* Header Row */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 3 }}
          >
            <Box>
              <Typography
                variant="body1"
                sx={{ fontWeight: 800, color: "#1E293B" }}
              >
                System Traffic Hub
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Real-time connection health and call analytics
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<Filter size={16} />}
              onClick={() => setIsDrawerOpen(true)}
              sx={{
                borderRadius: 2,
                bgcolor: COLORS.primary,
                textTransform: "none",
                px: 2,
              }}
            >
              Filters
            </Button>
          </Stack>

          {/* Empty State */}
          {!loading && stats.total === 0 ? (
            <EmptyState
              title="No Traffic Data"
              subtitle="There's no call traffic data available. Make some calls to see analytics here."
              icon="trend"
              onRefresh={fetchData}
            />
          ) : (
            <>
              {/* KPI Cards */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <KPICard
                    title="Total Calls"
                    value={stats.total}
                    subtitle="All traffic"
                    icon={Activity}
                    color={COLORS.primary}
                    loading={loading}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <KPICard
                    title="Avg Duration"
                    value={stats.avgDuration}
                    subtitle="Per call"
                    icon={Timer}
                    color={COLORS.cyan}
                    loading={loading}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <KPICard
                    title="Success Rate"
                    value={`${((stats.effective / stats.total) * 100 || 0).toFixed(1)}%`}
                    subtitle="Effective calls"
                    icon={Zap}
                    color={COLORS.success}
                    loading={loading}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <KPICard
                    title="Peak Hour"
                    value={stats.peakHour}
                    subtitle="Highest traffic"
                    icon={TrendingUp}
                    color={COLORS.warning}
                    loading={loading}
                  />
                </Grid>
              </Grid>

              {/* Charts Row */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} lg={8}>
                  <HourlyLoadChart data={stats.chartData} loading={loading} />
                </Grid>
                <Grid item xs={12} lg={4}>
                  <ConnectionHealthChart
                    data={stats.qualityData}
                    loading={loading}
                  />
                </Grid>
              </Grid>

              {/* Direction Split */}
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <DirectionSplitChart
                    inbound={stats.inCount}
                    outbound={stats.outCount}
                    total={stats.total}
                    loading={loading}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      border: "1px solid #E2E8F0",
                      height: "100%",
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 700, mb: 2 }}
                    >
                      Quick Stats
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Paper
                          sx={{
                            p: 2,
                            bgcolor: "#F8FAFC",
                            borderRadius: 2,
                            textAlign: "center",
                          }}
                        >
                          <Typography
                            variant="h4"
                            sx={{ fontWeight: 800, color: COLORS.primary }}
                          >
                            {stats.inCount}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: "#64748B" }}
                          >
                            Inbound Calls
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={6}>
                        <Paper
                          sx={{
                            p: 2,
                            bgcolor: "#F8FAFC",
                            borderRadius: 2,
                            textAlign: "center",
                          }}
                        >
                          <Typography
                            variant="h4"
                            sx={{ fontWeight: 800, color: COLORS.success }}
                          >
                            {stats.outCount}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: "#64748B" }}
                          >
                            Outbound Calls
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={6}>
                        <Paper
                          sx={{
                            p: 2,
                            bgcolor: "#F8FAFC",
                            borderRadius: 2,
                            textAlign: "center",
                          }}
                        >
                          <Typography
                            variant="h4"
                            sx={{ fontWeight: 800, color: COLORS.danger }}
                          >
                            {stats.silent}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: "#64748B" }}
                          >
                            Failed Calls
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={6}>
                        <Paper
                          sx={{
                            p: 2,
                            bgcolor: "#F8FAFC",
                            borderRadius: 2,
                            textAlign: "center",
                          }}
                        >
                          <Typography
                            variant="h4"
                            sx={{ fontWeight: 800, color: COLORS.success }}
                          >
                            {stats.effective}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: "#64748B" }}
                          >
                            Effective Calls
                          </Typography>
                        </Paper>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              </Grid>
            </>
          )}

          {/* Filter Drawer */}
          <Drawer
            anchor="right"
            open={isDrawerOpen}
            onClose={() => setIsDrawerOpen(false)}
          >
            <Box sx={{ width: 320, p: 3 }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 3 }}
              >
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Filter Timeframe
                </Typography>
                <IconButton onClick={() => setIsDrawerOpen(false)}>
                  <X size={20} />
                </IconButton>
              </Stack>
              <Stack spacing={2}>
                <TextField
                  type="date"
                  label="Start Date"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  value={filters.startDate}
                  onChange={(e) =>
                    setFilters((f) => ({ ...f, startDate: e.target.value }))
                  }
                />
                <TextField
                  type="date"
                  label="End Date"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  value={filters.endDate}
                  onChange={(e) =>
                    setFilters((f) => ({ ...f, endDate: e.target.value }))
                  }
                />
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => setIsDrawerOpen(false)}
                  sx={{
                    bgcolor: COLORS.primary,
                    py: 1.5,
                    borderRadius: 2,
                    fontWeight: 600,
                  }}
                >
                  Apply Filters
                </Button>
                <Button
                  fullWidth
                  variant="text"
                  onClick={() => {
                    setFilters({ startDate: "", endDate: "" });
                    setIsDrawerOpen(false);
                  }}
                  sx={{ color: "#64748B" }}
                >
                  Reset Filters
                </Button>
              </Stack>
            </Box>
          </Drawer>
        </Box>
      </Box>
    </Box>
  );
};

export default TrafficReport;
